import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown, Phone, Mail } from "lucide-react";
import { BUSINESS, SERVICE_GROUPS, PASSIVE_HOUSE_BADGE, RETROFITIQ_LOGO, RETROFITIQ_WORDMARK } from "@/data/content";
import { TEST_IDS } from "@/constants/testIds";
import { trackBookSurvey } from "@/lib/analytics";

// 3-category mega-menu: Investigate -> Design -> Verify. Column headers link to
// the new pillar pages; items link to existing service pages (no slugs changed).
const MEGA_MENU = [
  {
    title: "Building Performance Diagnostics",
    to: "/building-performance-diagnostics",
    phase: "Investigate",
    tagline: "Measure the real problem.",
    links: [
      ["Home Health Diagnostics", "/services/home-health-diagnostics"],
      ["Thermal Imaging", "/services/thermal-imaging"],
      ["Heat Loss Investigation", "/services/heat-loss-investigation"],
      ["Thermal Bridging Survey", "/services/thermal-bridging-survey"],
      ["Blower Door Testing", "/services/blower-door-testing"],
      ["Damp, Moisture & Condensation", "/services/damp-moisture-condensation"],
      ["Indoor Air Quality Assessments", "/services/indoor-air-quality"],
    ],
  },
  {
    title: "Building Performance Engineering",
    to: "/building-performance-engineering",
    phase: "Design",
    tagline: "Engineer the right solution.",
    links: [
      ["Engineering & Design Services", "/engineering-design-services"],
      ["Building Physics Assessment", "/building-physics-assessment"],
      ["Passive House Design", "/services/passive-house-design"],
      ["EnerPHit Retrofit Design", "/services/enerphit-retrofit-design"],
      ["PHPP Energy Modelling", "/services/phpp-energy-modelling"],
      ["Passive House Certification Support", "/services/passive-house-certification-support"],
      ["Heat Pump Readiness Assessment", "/services/heat-pump-readiness"],
      ["Insulation & Retrofit", "/services/insulation-retrofit"],
      ["External Wall Insulation & Cladding", "/services/external-wall-insulation-cladding"],
      ["MVHR & Ventilation", "/services/mvhr-ventilation"],
      ["Soundproofing & Acoustics", "/services/soundproofing-acoustics"],
    ],
  },
  {
    title: "Building Performance Quality Assurance",
    to: "/building-performance-quality-assurance",
    phase: "Verify",
    tagline: "Prove the performance.",
    links: [],
  },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  // Mount-once scroll listener — onScroll is defined inside the effect and
  // setScrolled is a stable React setter; no deps required.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // When the mobile menu is open: flag it on <body> (so the bottom sticky
  // contact bar is hidden via CSS) and lock background scrolling. The
  // position:fixed technique reliably prevents background scroll on iOS Safari
  // and Android; the scroll position is preserved and restored on close.
  useEffect(() => {
    if (!mobileOpen) {
      document.body.classList.remove("riq-menu-open");
      return undefined;
    }
    const scrollY = window.scrollY;
    document.body.classList.add("riq-menu-open");
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    return () => {
      const top = document.body.style.top;
      document.body.classList.remove("riq-menu-open");
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      window.scrollTo(0, top ? -parseInt(top, 10) : 0);
    };
  }, [mobileOpen]);

  const closeMenus = () => {
    setMobileOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  };

  const transparent = isHome && !scrolled;
  const headerBase = transparent
    ? "bg-transparent text-white border-transparent"
    : "bg-white text-slate-900 border-slate-200 shadow-sm";

  const linkBase = transparent ? "text-white/85 hover:text-white" : "text-slate-700 hover:text-slate-900";
  const activeLink = transparent ? "text-white" : "text-slate-900";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 border-b transition-colors duration-300 ${headerBase}`}
    >
      {/* Top contact strip — high-visibility phone, email and address. Hidden on small screens. */}
      <div className={`hidden md:block border-b ${transparent ? "border-white/10 bg-black/30 backdrop-blur-sm" : "border-slate-100 bg-slate-50"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-9 flex items-center justify-between text-xs">
          <div className="flex items-center gap-5">
            <a
              data-testid="header-phone"
              href={BUSINESS.phoneHref}
              className={`flex items-center gap-2 font-mono font-semibold ${transparent ? "text-white" : "text-slate-900"}`}
            >
              <Phone className="w-3.5 h-3.5 text-[#16A34A]" />
              {BUSINESS.phone}
            </a>
            <a
              data-testid="header-email"
              href={BUSINESS.emailHref}
              className={`hidden lg:flex items-center gap-2 ${transparent ? "text-white/85 hover:text-white" : "text-slate-700 hover:text-slate-900"}`}
            >
              <Mail className="w-3.5 h-3.5 text-[#DC2626]" />
              {BUSINESS.email}
            </a>
          </div>
          <div className={`font-mono uppercase tracking-widest text-[10px] ${transparent ? "text-white/60" : "text-slate-500"}`}>
            London · Essex · Kent · Surrey · Hertfordshire · Certified Passive House Designer · Fully Insured
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-6">
        <div className="flex items-center gap-3">
        <Link to="/" data-testid={TEST_IDS.nav.logo} className="flex items-center gap-3">
          {/* Colour RIQ icon — always visible. The black square is part of the brand mark; we crop to a circle for cleaner placement on light backgrounds. */}
          <img
            src={RETROFITIQ_LOGO.src}
            alt={RETROFITIQ_LOGO.alt}
            className="w-[39px] h-[39px] md:w-[42px] md:h-[42px] object-cover rounded-full shrink-0 ring-2 ring-white/10"
            loading="eager"
            width="42"
            height="42"
          />
          {/* Brand wordmark in HTML (red Retro · green Fit · red IQ) — sharp on any background */}
          <div className="hidden sm:block leading-tight">
            <div className="font-heading font-extrabold tracking-tight text-lg md:text-xl">
              <span className="text-[#DC2626]">Retro</span><span className="text-[#16A34A]">Fit</span><span className="text-[#DC2626]">IQ</span>
            </div>
            <div className={`overline ${transparent ? "text-white/60" : "text-slate-500"}`}>
              Building Performance
            </div>
          </div>
        </Link>
          {/* Compact Certified Passive House Designer badge — visible across all pages */}
          <a
            href={PASSIVE_HOUSE_BADGE.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="passive-house-badge-header"
            title={PASSIVE_HOUSE_BADGE.alt}
            aria-label={PASSIVE_HOUSE_BADGE.alt}
            className={`hidden xl:inline-flex items-center gap-2 ml-2 pl-2 pr-3 py-1 border transition-colors ${
              transparent ? "border-white/20 bg-white/[0.06] hover:bg-white/[0.12]" : "border-slate-200 bg-slate-50 hover:bg-slate-100"
            }`}
          >
            <img src={PASSIVE_HOUSE_BADGE.src} alt={PASSIVE_HOUSE_BADGE.alt} className="w-7 h-7 object-contain shrink-0" loading="eager" width="28" height="28" />
            <span className="leading-tight">
              <span className={`block font-mono text-[8px] tracking-widest uppercase ${transparent ? "text-white/60" : "text-slate-500"}`}>
                Certified
              </span>
              <span className={`block text-[10px] font-semibold ${transparent ? "text-white" : "text-slate-900"}`}>
                Passive House Designer
              </span>
            </span>
          </a>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-[22px] text-sm font-medium">
          <NavLink data-testid={TEST_IDS.nav.home} to="/" end className={({ isActive }) => `${linkBase} ${isActive ? activeLink : ""}`}>Home</NavLink>
          <NavLink data-testid={TEST_IDS.nav.about} to="/about" className={({ isActive }) => `${linkBase} ${isActive ? activeLink : ""}`}>About</NavLink>

          <div className="relative" onMouseLeave={() => setServicesOpen(false)}>
            <button
              data-testid={TEST_IDS.nav.services}
              onClick={() => setServicesOpen((v) => !v)}
              onMouseEnter={() => setServicesOpen(true)}
              className={`flex items-center gap-1 ${linkBase}`}
            >
              Services <ChevronDown className="w-4 h-4" />
            </button>
            {servicesOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[min(92vw,860px)]">
                <div className="bg-white border border-slate-200 shadow-xl p-5 rounded-sm grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="mega-menu">
                  {MEGA_MENU.map((col) => (
                    <div key={col.to}>
                      <Link
                        to={col.to}
                        onClick={closeMenus}
                        data-testid={`mega-col-${col.phase.toLowerCase()}`}
                        className="group flex items-center gap-2 text-slate-900 font-heading font-semibold text-sm hover:text-[#16A34A] transition-colors"
                      >
                        <span className="font-mono text-[9px] uppercase tracking-widest text-[#16A34A]">{col.phase}</span>
                      </Link>
                      <Link to={col.to} onClick={closeMenus} className="block mt-1 text-slate-900 font-heading font-semibold text-sm hover:text-[#16A34A] transition-colors">
                        {col.title}
                      </Link>
                      {col.tagline && (
                        <p data-testid={`mega-tagline-${col.phase.toLowerCase()}`} className="mt-0.5 text-[11px] font-semibold text-[#B91C1C] leading-snug">
                          {col.tagline}
                        </p>
                      )}
                      <div className="mt-2 flex flex-col border-l-2 border-slate-100 pl-3">
                        {col.links.map(([label, to]) => (
                          <Link key={to} to={to} onClick={closeMenus} className="py-1 text-[13px] text-slate-600 hover:text-[#16A34A] transition-colors">
                            {label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <NavLink data-testid="nav-investigation" to="/building-performance-investigation" className={({ isActive }) => `${linkBase} ${isActive ? activeLink : ""} whitespace-nowrap`}>Building Investigation</NavLink>
          <NavLink data-testid="nav-comparisons" to="/comparisons" className={({ isActive }) => `${linkBase} ${isActive ? activeLink : ""}`}>Comparisons</NavLink>
          <NavLink data-testid="nav-problems" to="/problems" className={({ isActive }) => `${linkBase} ${isActive ? activeLink : ""} flex flex-col items-center text-center leading-[1.05]`}><span>House</span><span>Problems</span></NavLink>
          <NavLink data-testid="nav-areas" to="/areas" className={({ isActive }) => `${linkBase} ${isActive ? activeLink : ""}`}>Areas</NavLink>
          <NavLink data-testid={TEST_IDS.nav.caseStudies} to="/case-studies" className={({ isActive }) => `${linkBase} ${isActive ? activeLink : ""}`}>Case Studies</NavLink>
          <NavLink data-testid="nav-investigations" to="/investigations" className={({ isActive }) => `${linkBase} ${isActive ? activeLink : ""}`}>Investigations</NavLink>
          <NavLink data-testid="nav-public-gallery" to="/gallery" className={({ isActive }) => `${linkBase} ${isActive ? activeLink : ""} whitespace-nowrap`}>Public Gallery</NavLink>
          <NavLink data-testid="nav-learn" to="/learn" className={({ isActive }) => `${linkBase} ${isActive ? activeLink : ""}`}>Academy</NavLink>
          <NavLink data-testid="nav-professionals" to="/professionals" className={({ isActive }) => `${linkBase} ${isActive ? activeLink : ""}`}>Who We Help</NavLink>
          <NavLink data-testid={TEST_IDS.nav.contact} to="/contact" className={({ isActive }) => `${linkBase} ${isActive ? activeLink : ""}`}>Contact</NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid={TEST_IDS.nav.cta}
            onClick={() => trackBookSurvey("header_desktop")}
            className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-[#15803d] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors shadow-md shadow-[#16A34A]/30"
          >
            Book a Survey
          </Link>
        </div>

        <button
          data-testid={TEST_IDS.nav.mobileToggle}
          className="lg:hidden p-2"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile */}
      {mobileOpen && (
        <div className="lg:hidden bg-white text-slate-900 border-t border-slate-200">
          <nav
            data-testid="mobile-menu"
            className="px-6 py-4 flex flex-col gap-1 text-sm max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain pb-10"
          >
            {/* Prominent mobile contact CTAs */}
            <div className="grid grid-cols-2 gap-2 pb-3 mb-2 border-b border-slate-100">
              <a href={BUSINESS.phoneHref} onClick={closeMenus} data-testid="mobile-phone-cta" className="inline-flex items-center justify-center gap-2 bg-[#16A34A] text-white px-3 py-2.5 text-xs font-semibold uppercase tracking-wider">
                <Phone className="w-4 h-4" /> Call
              </a>
              <a href={BUSINESS.emailHref} onClick={closeMenus} data-testid="mobile-email-cta" className="inline-flex items-center justify-center gap-2 bg-[#DC2626] text-white px-3 py-2.5 text-xs font-semibold uppercase tracking-wider">
                <Mail className="w-4 h-4" /> Email
              </a>
            </div>

            <Link to="/" onClick={closeMenus} data-testid="mobile-link-home" className="py-2.5 border-b border-slate-100">Home</Link>
            <Link to="/about" onClick={closeMenus} data-testid="mobile-link-about" className="py-2.5 border-b border-slate-100">About</Link>

            {/* Services — collapsible so it never blocks the links below */}
            <div className="border-b border-slate-100">
              <button
                type="button"
                data-testid="mobile-services-toggle"
                onClick={() => setMobileServicesOpen((v) => !v)}
                aria-expanded={mobileServicesOpen}
                className="w-full flex items-center justify-between py-2.5 text-left text-slate-900"
              >
                <span>Services</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileServicesOpen && (
                <div data-testid="mobile-services-list" className="flex flex-col pb-2 pl-3 border-l-2 border-[#16A34A]/30 ml-1">
                  {MEGA_MENU.map((col) => (
                    <div key={col.to} className="py-1">
                      <Link to={col.to} onClick={closeMenus} className="block py-1.5">
                        <span className="block font-mono text-[9px] uppercase tracking-widest text-[#16A34A]">{col.phase}</span>
                        <span className="block font-heading font-semibold text-slate-900">{col.title}</span>
                        {col.tagline && <span className="block text-[11px] font-semibold text-[#B91C1C] leading-snug">{col.tagline}</span>}
                      </Link>
                      {col.links.map(([label, to]) => (
                        <Link key={to} to={to} onClick={closeMenus} className="block py-1.5 pl-3 text-slate-600 hover:text-slate-900">{label}</Link>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Link to="/building-performance-investigation" onClick={closeMenus} data-testid="mobile-link-investigation" className="py-2.5 border-b border-slate-100">Building Investigation</Link>
            <Link to="/comparisons" onClick={closeMenus} data-testid="mobile-link-comparisons" className="py-2.5 border-b border-slate-100">Comparisons</Link>
            <Link to="/problems" onClick={closeMenus} data-testid="mobile-link-problems" className="py-2.5 border-b border-slate-100">House Problems</Link>
            <Link to="/areas" onClick={closeMenus} data-testid="mobile-link-areas" className="py-2.5 border-b border-slate-100">Areas We Cover</Link>
            <Link to="/case-studies" onClick={closeMenus} data-testid="mobile-link-case-studies" className="py-2.5 border-b border-slate-100">Case Studies</Link>
            <Link to="/investigations" onClick={closeMenus} data-testid="mobile-link-investigations" className="py-2.5 border-b border-slate-100">Investigations</Link>
            <Link to="/learn" onClick={closeMenus} data-testid="mobile-link-academy" className="py-2.5 border-b border-slate-100">Academy</Link>
            <Link to="/faq" onClick={closeMenus} data-testid="mobile-link-faq" className="py-2.5 border-b border-slate-100">FAQ</Link>
            <Link to="/gallery" onClick={closeMenus} data-testid="mobile-link-gallery" className="py-2.5 border-b border-slate-100">Public Gallery</Link>
            <Link to="/reviews" onClick={closeMenus} data-testid="mobile-link-reviews" className="py-2.5 border-b border-slate-100">Reviews</Link>
            <Link to="/professionals" onClick={closeMenus} data-testid="mobile-link-who-we-help" className="py-2.5 border-b border-slate-100">Who We Help</Link>
            <Link to="/contact" onClick={closeMenus} data-testid="mobile-link-contact" className="py-2.5">Contact</Link>

            <Link
              to="/contact"
              onClick={() => { closeMenus(); trackBookSurvey("header_mobile"); }}
              data-testid="mobile-book-survey"
              className="mt-3 inline-flex justify-center bg-[#16A34A] text-white px-5 py-3 text-xs font-semibold uppercase tracking-wider"
            >
              Book a Survey
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
